import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Category,
  GridView,
  Layers,
  MoreVert,
} from '@mui/icons-material';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import BookingCalendar from '../booking/BookingCalendar';
import QuickBookingDrawer from '../dashboard/QuickBookingDrawer';
import { dateToMoment, momentToDate } from '../../utils/dateUtils';
import {
  getMobileDashboardPanelSx,
  getMobileDashboardScrollSx,
  mobileAppGlassCard,
} from '../layout/mobile/mobileAppTheme';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';
import { MOBILE_DASHBOARD_MODES } from './mobileDashboardModes';
import { CALENDAR_GRID_DAY_COUNT } from '../../utils/dateUtils';

const GROUP_VIEW_LABELS = {
  full: 'Full View',
  floor: 'Floor View',
  room_type: 'Room Type View',
};

const GROUP_VIEW_ICONS = {
  full: GridView,
  floor: Layers,
  room_type: Category,
};

const dateFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: '#f8fafc',
    fontSize: '0.82rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(76, 225, 182, 0.35)' },
    '&.Mui-focused fieldset': { borderColor: LOGIN_ACCENT },
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    textAlign: 'center',
  },
  '& .MuiSvgIcon-root': { color: 'rgba(148,163,184,0.9)' },
};

const VIEW_HINTS = {
  ultra: 'Tap a room to view booking · pinch-scroll the grid',
  calendar: '7-day room grid · tap empty cell to quick book',
  month: 'Tap any day for stats · full month at a glance',
};

export default function MobileBookingCalendarShell({
  displayView,
  currentDate,
  onDateChange,
  groupView,
  onGroupViewChange,
  themeMode = 'dark',
  onQuickBook,
  onWebCheckIn,
  onOpenWebCheckInInvite,
  onCheckout,
  onCheckIn,
  onEventBlock,
  onHousekeeping,
  onDirtyRoomHousekeeping,
  pendingCleaningByRoomId = {},
  quickBookContext,
  onQuickBookClose,
  onQuickBookSuccess,
  dataRevision = 0,
}) {
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const panelMeta = useMemo(() => {
    return MOBILE_DASHBOARD_MODES.find((entry) => entry.displayView === displayView) || null;
  }, [displayView]);

  const currentDateMoment = useMemo(() => dateToMoment(currentDate), [currentDate]);
  const datePickerFormat = displayView === 'month' ? 'MMMM YYYY' : 'DD MMM YYYY';
  const datePickerViews = displayView === 'month' ? ['year', 'month'] : ['year', 'month', 'day'];

  const shiftDate = (direction) => {
    const currentMoment = moment(currentDate);
    let nextMoment = currentMoment.clone();

    if (displayView === 'month') {
      nextMoment = direction < 0
        ? currentMoment.clone().subtract(1, 'month')
        : currentMoment.clone().add(1, 'month');
    } else {
      const days = displayView === 'ultra' ? 1 : CALENDAR_GRID_DAY_COUNT;
      nextMoment = direction < 0
        ? currentMoment.clone().subtract(days, 'days')
        : currentMoment.clone().add(days, 'days');
    }

    const dateObj = momentToDate(nextMoment);
    if (dateObj) onDateChange(dateObj);
  };

  return (
    <Box sx={getMobileDashboardPanelSx()}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            ...mobileAppGlassCard,
            mx: 1.25,
            mb: 0.75,
            p: 0.85,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.55,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.2 }}>
                {panelMeta?.label || 'Booking'} view
              </Typography>
              <Typography sx={{ color: 'rgba(148,163,184,0.85)', fontSize: '0.65rem', mt: 0.15 }}>
                {VIEW_HINTS[displayView] || 'Booking calendar'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(event) => setGroupMenuAnchor(event.currentTarget)}
              sx={{
                color: 'rgba(148,163,184,0.95)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                flexShrink: 0,
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={groupMenuAnchor}
              open={Boolean(groupMenuAnchor)}
              onClose={() => setGroupMenuAnchor(null)}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  minWidth: 200,
                  backgroundColor: '#232329',
                  border: '1px solid rgba(255,255,255,0.08)',
                },
              }}
            >
              {Object.keys(GROUP_VIEW_LABELS).map((key) => {
                const Icon = GROUP_VIEW_ICONS[key];
                return (
                  <MenuItem
                    key={key}
                    selected={groupView === key}
                    onClick={() => {
                      onGroupViewChange(key);
                      setGroupMenuAnchor(null);
                    }}
                    sx={{ color: '#e2e8f0', fontSize: '0.875rem' }}
                  >
                    <ListItemIcon>
                      <Icon sx={{ color: LOGIN_ACCENT, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>{GROUP_VIEW_LABELS[key]}</ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <IconButton
              onClick={() => shiftDate(-1)}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#e2e8f0',
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DatePicker
                value={currentDateMoment}
                views={datePickerViews}
                openTo={displayView === 'month' ? 'month' : 'day'}
                onChange={(newDate) => {
                  if (newDate?.isValid?.()) {
                    const dateObj = momentToDate(newDate);
                    if (dateObj) onDateChange(dateObj);
                  }
                }}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: dateFieldSx,
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={() => shiftDate(1)}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#e2e8f0',
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </LocalizationProvider>

      <Box
        data-mobile-dashboard-scroll=""
        data-dashboard-room-board-root=""
        sx={{
          ...getMobileDashboardScrollSx(),
          mx: displayView === 'month' ? 0.75 : 0.5,
          mb: 0.5,
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
          p: 0,
        }}
      >
        <BookingCalendar
          displayView={displayView}
          currentDate={currentDate}
          onDateChange={onDateChange}
          groupView={groupView}
          theme="dark"
          layoutVariant="mobile"
          onQuickBook={onQuickBook}
          onWebCheckIn={onWebCheckIn}
          onOpenWebCheckInInvite={onOpenWebCheckInInvite}
          onCheckout={onCheckout}
          onCheckIn={onCheckIn}
          onEventBlock={onEventBlock}
          onHousekeeping={onHousekeeping}
          onDirtyRoomHousekeeping={onDirtyRoomHousekeeping}
          pendingCleaningByRoomId={pendingCleaningByRoomId}
          dataRevision={dataRevision}
        />
      </Box>

      <QuickBookingDrawer
        open={Boolean(quickBookContext)}
        context={quickBookContext}
        onClose={onQuickBookClose}
        onSuccess={onQuickBookSuccess}
        themeMode={themeMode}
      />
    </Box>
  );
}
